import React, { useState } from 'react';
import {
  Box, Card, CardContent, TextField, Button,
  Typography, Alert, Container, IconButton, InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { studentPortalAPI } from '../../services/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useStudentAuth } from '../../hooks/useStudentAuth';

export default function StudentLogin() {
  const navigate = useNavigate();
  const { login } = useStudentAuth();
  const [searchParams] = useSearchParams();

  const [tenantCode, setTenantCode]     = useState(searchParams.get('school') || '');
  const [form, setForm]                 = useState({ registration_no: '', password: '' });
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!tenantCode.trim()) {
      setError('Please enter your school ID.');
      return;
    }

    const tid = tenantCode.trim();
    setLoading(true);
    try {
      // Pass X-Tenant directly — don't pollute localStorage before auth succeeds
      const { data } = await studentPortalAPI.login(form, { headers: { 'X-Tenant': tid } });
      localStorage.setItem('tenant_id', tid);
      login(data.student, data.token, data.expires_at);

      if (data.student.must_change_password) {
        navigate('/student/change-password');
      } else {
        navigate('/student/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Check your school ID, student number and password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ p: { xs: 1, sm: 2 } }}>
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="100vh">
        <Card sx={{ width: '100%', maxWidth: 400 }}>
          <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
              <Box component="img" src="/Logo.png" alt="School Sync" sx={{ width: 80, height: 80, objectFit: 'contain', mb: 2 }} />
              <Typography variant="h5" fontWeight={600} color="primary">Student Portal</Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>Sign in with your student number</Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="School ID"
                value={tenantCode}
                onChange={(e) => setTenantCode(e.target.value)}
                required
                margin="normal"
                helperText="Enter the school ID provided by your school"
              />
              <TextField
                fullWidth
                label="Student Number"
                placeholder="e.g. 25/SMS/ST0001"
                value={form.registration_no}
                onChange={(e) => setForm((p) => ({ ...p, registration_no: e.target.value }))}
                required
                margin="normal"
                autoComplete="username"
              />
              <TextField
                fullWidth
                type={showPassword ? 'text' : 'password'}
                label="Password"
                value={form.password}
                onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                required
                margin="normal"
                autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((s) => !s)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button type="submit" fullWidth variant="contained" size="large" disabled={loading} sx={{ mt: 3, mb: 2 }}>
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}
